import React, {useRef, useState} from 'react'

import classNames from 'classnames'
import TagManager from 'react-gtm-module'
import {useEffectOnce, useUpdateEffect} from 'react-use'
import {palette} from 'styled-tools'
import styled from 'styled-components'
import {media} from 'styled-bootstrap-grid'
import HeartFillIcon from 'remixicon-react/HeartFillIcon'
import HeartLineIcon from 'remixicon-react/HeartLineIcon'
import HistoryLineIcon from 'remixicon-react/HistoryLineIcon'
import InformationLineIcon from 'remixicon-react/InformationLineIcon'
import ArrowLeftRightLineIcon from 'remixicon-react/ArrowLeftRightLineIcon'

import {ProductDetail, User, Variant} from '@festi/common/api/rest'
import {MainButton, MainIconButton} from '@festi/common/components/buttons'
import {
  PSmall,
  PriceText,
  OldPriceText,
  DutyfreeText,
} from '@festi/common/components/typography'
import {useAuth, useBambuser, useComparison} from '@festi/common/contexts'
import {onDutyFree} from '@festi/common/constants/channels'
import {Inventory} from '@festi/common/utils/products'
import {AugmentedUserPrice} from '@festi/common/utils/rest'
import {useWishlist} from '@festi/common/utils/wishlists'
import {useOnScreen} from '@festi/common/utils/useOnScreen'
import {formatPrice} from '@festi/utils/numbers'
import settings from '@festi/common/constants/settings'
import {getListingPrice} from '@festi/common/utils/price'
import {AddToCartButton} from '@festi/common/components/cart'
import {styledTheme} from '@festi/common/themes'
import {fluidRange} from '@festi/common/utils/styles'

import PriceHistoryModal from '../../PriceHistoryModal'
import ProductRichText from './NewProductRichText'
import NotificationSubscription from './NewNotificationSubscription'
import ProductTitle from './ProductTitle'
import ProductStock from './NewProductStock'
import PayLettkaupCalculator from './NewPayLettkaupCalculator'
import ProductInfoScrollRow from './ProductInfoScrollRow'
import InfoBox from 'src/components/common/redesign/InfoBox'
import QsPricer from '../../pricer/QsPricer'

const MINIMUM_SIMINN_PAY_PRICE = 20000

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  padding-bottom: 24px;
  scroll-behavior: smooth;

  ${media.md`
    gap: 40px;
    padding-bottom: 0;
  `}
`
const DesktopTopSectionWrapper = styled.div`
  display: none;

  ${media.md`
    display: flex;
    flex-direction: column;
    gap: 20px;
  `}
`

const PricingContainer = styled.div`
  width: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${media.md`
    gap: 4px;

    &.notDutyFree {
      flex-direction: row;
      align-items: center;
      gap: 16px;
    }
  `}
`

const FitContent = styled.div`
  width: fit-content;
`

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const DutyFreePriceWarning = styled(PSmall)`
  color: ${palette('ui60Solid')};
  width: 100%;
  padding-top: 2px;
  white-space: break-spaces;
`

const ButtonWrapper = styled.div`
  display: flex;
  gap: 8px;

  ${media.md`
    display: grid;
    grid-template-columns: 1fr 1fr;
  `}
`

const WishlistBtnWrapper = styled.div`
  display: none;

  &.wishlistMobile {
    display: block;
  }

  ${media.md`
    display: block;

    &.wishlistMobile {
      display: none;
    }
  `}
`

const InfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  ${media.md`
    padding-top: 4px;
  `}
`

const InfoText = styled.span`
  color: ${palette('blue')};
  font-size: 14px;
`

const GreyContainer = styled.div`
  background-color: ${palette('backgroundGrey')};
  border-radius: 16px;
`

const GreyLine = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${palette('ui10Solid')};
`

const GreyInnerPadding = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;

  ${media.md`
    padding: 28px;
  `}
`

const StrikeThrough = styled.span`
  position: relative;

  &:before {
    content: '';
    display: block;
    position: absolute;
    top: 42%;
    left: -2px;
    right: -2px;
    height: 4px;
    background-color: ${palette('pink')};
    z-index: -1;
  }
`

interface PriceProps {
  sku: string
  price: number
  lowestPrice: number
  cityPrice: number | null
  cityBasePrice: number | null
  onOpenPriceHistory: () => void
}

function PriceInfo({
  price,
  cityPrice,
  lowestPrice,
  cityBasePrice,
  onOpenPriceHistory,
}: PriceProps): JSX.Element {
  const hasDiscount = lowestPrice < price
  const cityHasDiscount = cityPrice && cityPrice < cityBasePrice

  const showPriceHistory = true

  const OldPrice = ({hasDiscount}) => {
    return onDutyFree ? (
      <DutyfreeText className="newDesign">
        {!hasDiscount ? (
          <>
            <p>Almennt verð {formatPrice(cityPrice)}</p>
            {cityHasDiscount && (
              <DutyFreePriceWarning>
                Vinsamlega athugið að þessi vara er á tilboðsverði í bænum
              </DutyFreePriceWarning>
            )}
          </>
        ) : (
          <p>
            Verð áður <StrikeThrough>{formatPrice(price)}</StrikeThrough>
          </p>
        )}
      </DutyfreeText>
    ) : (
      !!hasDiscount && (
        <OldPriceText oldPriceSize={fluidRange('font-size', '18px', '20px')}>
          {formatPrice(price)}
        </OldPriceText>
      )
    )
  }

  return (
    <PricingContainer className={classNames({cityHasDiscount})}>
      <PriceWrapper className={classNames({notDutyFree: !onDutyFree})}>
        <FitContent>
          <PriceText size={fluidRange('font-size', '40px', '48px')}>
            {formatPrice(lowestPrice)}
          </PriceText>
        </FitContent>
        <FitContent>
          <OldPrice hasDiscount={hasDiscount} />
        </FitContent>
      </PriceWrapper>

      {showPriceHistory && (
        <MainButton
          label="Sjá verðsögu"
          onClick={onOpenPriceHistory}
          prefixIcon={HistoryLineIcon}
          buttonVariant="ghost"
          size="small"
        />
      )}
    </PricingContainer>
  )
}

interface Props {
  user: User
  product: ProductDetail
  variant: Variant
  inventory: Inventory[]
  userPrice: AugmentedUserPrice
}

export default function ProductInfo({
  user,
  product,
  variant,
  inventory,
  userPrice,
}: Props): JSX.Element {
  const {promptAuth, setPromptAuth} = useAuth()
  const {isVariantInWishlist, toggleVariantInWishlist} = useWishlist()
  const {comparedSkus, addToComparisonNew, removeFromComparison} =
    useComparison()
  const {setChangeChatBubblePosition} = useBambuser()

  const [priceHistoryOpen, setPriceHistoryOpen] = useState<boolean>(false)

  const divOnScreenRef = useRef<HTMLDivElement>(null)
  const isInView = useOnScreen(divOnScreenRef)

  useUpdateEffect(() => {
    setChangeChatBubblePosition(!isInView)
  }, [isInView, setChangeChatBubblePosition])

  // NOTE(Bjorg): This is a hack to prevent a server/client prop not matching error
  // for comparison icon color. Is there a better way?
  const [hasMounted, setHasMounted] = React.useState(false)
  useEffectOnce(() => {
    setHasMounted(true)
  })

  const inComparison = hasMounted
    ? comparedSkus?.indexOf(variant.sku) !== -1
    : undefined

  const {
    sku,
    name,
    showcases,
    importantInfo,
    subscriptionStatus,
    isInStockWeb,
    isInStockDutyfree,
  } = variant

  const price = userPrice?.price?.price || getListingPrice(variant)?.price || 0
  const lowestPrice =
    userPrice?.lowestPrice || getListingPrice(variant)?.lowestPrice || 0
  const cityPrice = onDutyFree
    ? variant?.listings?.webshop?.price?.lowestPrice
    : null
  const cityBasePrice = onDutyFree
    ? variant?.listings?.webshop?.price?.price
    : null

  const outOfStockWeb = !(onDutyFree ? isInStockDutyfree : isInStockWeb)

  const activeProduct = !!variant?.listings?.[settings?.channel]?.active

  const openPriceHistory = () => {
    setPriceHistoryOpen(true)

    TagManager.dataLayer({
      dataLayer: {
        category: 'Price History',
        event: 'Opened',
        event_label: sku,
      },
      dataLayerName: 'dataLayer',
    })
  }

  return (
    <>
      <Wrapper>
        <PriceHistoryModal
          sku={sku}
          name={product.name}
          isOpen={priceHistoryOpen}
          onClose={() => setPriceHistoryOpen(false)}
        />

        <DesktopTopSectionWrapper>
          <ProductTitle name={name} sku={sku} />
          <ProductRichText html={product.shortDescription} />
        </DesktopTopSectionWrapper>

        {importantInfo && <InfoBox info={importantInfo} variant="warning" />}

        {activeProduct && (
          <div>
            <PriceInfo
              sku={sku}
              price={price}
              lowestPrice={lowestPrice}
              cityPrice={cityPrice}
              cityBasePrice={cityBasePrice}
              onOpenPriceHistory={openPriceHistory}
            />
            <div ref={divOnScreenRef} />
          </div>
        )}

        {activeProduct && (
          <ActionsWrapper>
            <AddToCartButton
              variant={variant}
              product={product}
              newDesign
              large
            />
            <ButtonWrapper>
              <MainButton
                label="Setja í samanburð"
                onClick={
                  inComparison
                    ? () => removeFromComparison(sku)
                    : () => addToComparisonNew(product, variant)
                }
                suffixIcon={ArrowLeftRightLineIcon}
                iconColor={
                  inComparison
                    ? styledTheme.palette.lightBlue
                    : styledTheme.palette.ui20Solid
                }
                buttonVariant="ghost"
                fullWidth
              />

              <WishlistBtnWrapper>
                <MainButton
                  label="Óskalisti"
                  onClick={() =>
                    !user
                      ? setPromptAuth(!promptAuth)
                      : toggleVariantInWishlist(variant)
                  }
                  suffixIcon={
                    isVariantInWishlist(sku) ? HeartFillIcon : HeartLineIcon
                  }
                  iconColor={
                    isVariantInWishlist(sku)
                      ? styledTheme.palette.lightBlue
                      : styledTheme.palette.ui20Solid
                  }
                  buttonVariant="ghost"
                  fullWidth
                />
              </WishlistBtnWrapper>
              <WishlistBtnWrapper
                className={classNames({wishlistMobile: true})}
              >
                <MainIconButton
                  onClick={() =>
                    !user
                      ? setPromptAuth(!promptAuth)
                      : toggleVariantInWishlist(variant)
                  }
                  icon={
                    isVariantInWishlist(sku) ? HeartFillIcon : HeartLineIcon
                  }
                  iconColor={
                    isVariantInWishlist(sku)
                      ? styledTheme.palette.lightBlue
                      : styledTheme.palette.ui20Solid
                  }
                  buttonVariant="ghost"
                />
              </WishlistBtnWrapper>
            </ButtonWrapper>

            {onDutyFree && (
              <InfoWrapper>
                <InformationLineIcon
                  size={18}
                  color={styledTheme.palette.ui50Solid}
                />
                <InfoText>
                  Aðeins hægt að panta og sækja í brottfararverslun.
                </InfoText>
              </InfoWrapper>
            )}
          </ActionsWrapper>
        )}

        {!onDutyFree && outOfStockWeb && (
          <NotificationSubscription sku={sku} status={subscriptionStatus} />
        )}

        {activeProduct && (
          <GreyContainer>
            <GreyInnerPadding>
              <ProductStock
                inventory={inventory}
                showcases={showcases}
                isInStock={isInStockWeb}
                showShowcases={!!product.collectAtWarehouse}
              />
            </GreyInnerPadding>

            {!(!variant?.shelfLabels?.length || onDutyFree) && (
              <>
                <GreyLine />

                <GreyInnerPadding>
                  <QsPricer product={product} variant={variant} />
                </GreyInnerPadding>
              </>
            )}
          </GreyContainer>
        )}

        {lowestPrice >= MINIMUM_SIMINN_PAY_PRICE && activeProduct && (
          <GreyContainer>
            <GreyInnerPadding>
              <PayLettkaupCalculator price={lowestPrice} />
            </GreyInnerPadding>
          </GreyContainer>
        )}
      </Wrapper>

      {activeProduct && (
        <ProductInfoScrollRow
          user={user}
          price={price}
          lowestPrice={lowestPrice}
          variant={variant}
          product={product}
          isInView={isInView}
        />
      )}
    </>
  )
}
